/**
 * Per-organization JWT cache in localStorage to reduce GET /api/get/user_jwt traffic.
 * Key: `data-hub:${organization_id}` — value JSON { jwt, created_at, updated_at }.
 */

import { normalize_data_hub_organization_id_string, hmac_param_name_to_plain_field_name } from './action_request_helpers';
import {
	data_hub_privacy_policy_allows_browser_storage,
} from './data_hub_privacy_policy';
import { allowed_all_hmac_key_names } from './static_vars';

export const organization_jwt_local_storage_ttl_ms = 60 * 60 * 1000;

/**
 * @param {string|null|undefined} organization_id
 * @returns {string|null}
 */
export function get_organization_jwt_local_storage_key(organization_id) {
	const normalized = normalize_data_hub_organization_id_string(organization_id);
	if (!normalized) {
		return null;
	}
	return `data-hub:${normalized}`;
}

/**
 * @param {string} jwt_string
 * @returns {Object|null} Unverified `payload.data` from JWT or null.
 */
export function decode_unverified_jwt_data_payload(jwt_string) {
	if (!jwt_string || typeof jwt_string !== 'string') {
		return null;
	}
	const segments = jwt_string.split('.');
	if (segments.length < 2) {
		return null;
	}
	try {
		let base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
		const pad = base64.length % 4;
		if (pad) {
			base64 += '='.repeat(4 - pad);
		}
		const json_payload = JSON.parse(atob(base64));
		const data = json_payload?.data;
		if (!data || typeof data !== 'object' || Array.isArray(data)) {
			return null;
		}
		return data;
	} catch {
		return null;
	}
}

/**
 * @param {string} jwt_string
 * @returns {number|null} JWT `iat` in milliseconds or null.
 */
export function read_unverified_jwt_issued_at_ms(jwt_string) {
	if (!jwt_string || typeof jwt_string !== 'string') {
		return null;
	}
	const segments = jwt_string.split('.');
	if (segments.length < 2) {
		return null;
	}
	try {
		let base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
		const pad = base64.length % 4;
		if (pad) {
			base64 += '='.repeat(4 - pad);
		}
		const json_payload = JSON.parse(atob(base64));
		const iat = json_payload?.iat;
		if (typeof iat !== 'number' || !Number.isFinite(iat)) {
			return null;
		}
		return iat * 1000;
	} catch {
		return null;
	}
}

/**
 * @param {Object} cache_entry
 * @param {number} [ttl_ms]
 * @returns {boolean}
 */
export function is_organization_jwt_cache_entry_within_ttl(cache_entry, ttl_ms = organization_jwt_local_storage_ttl_ms) {
	if (!cache_entry || typeof cache_entry !== 'object') {
		return false;
	}
	const now = Date.now();
	const updated_at = typeof cache_entry.updated_at === 'number' ? cache_entry.updated_at : null;
	const jwt_fallback = typeof cache_entry.jwt === 'string' ? read_unverified_jwt_issued_at_ms(cache_entry.jwt) : null;
	const reference_time = updated_at ?? jwt_fallback;
	if (reference_time === null || reference_time === undefined) {
		return false;
	}
	return now - reference_time < ttl_ms;
}

/**
 * @param {string|null|undefined} organization_id
 * @returns {Object|null}
 */
export function read_organization_jwt_cache_entry(organization_id) {
	const key = get_organization_jwt_local_storage_key(organization_id);
	if (!key || typeof localStorage === 'undefined') {
		return null;
	}
	try {
		const raw = localStorage.getItem(key);
		if (!raw) {
			return null;
		}
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object' || typeof parsed.jwt !== 'string') {
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
}

/**
 * @param {string|null|undefined} organization_id
 */
export function remove_organization_jwt_cache_entry(organization_id) {
	const key = get_organization_jwt_local_storage_key(organization_id);
	if (!key || typeof localStorage === 'undefined') {
		return;
	}
	try {
		localStorage.removeItem(key);
	} catch {
		// storage blocked
	}
}

/**
 * @param {Object} data_hub_instance
 * @param {string} jwt_string
 */
export function write_organization_jwt_cache_entry(data_hub_instance, jwt_string) {
	if (!data_hub_privacy_policy_allows_browser_storage(data_hub_instance)) {
		return;
	}
	if (!jwt_string || typeof jwt_string !== 'string') {
		return;
	}
	const key = get_organization_jwt_local_storage_key(data_hub_instance?.organization_id);
	if (!key || typeof localStorage === 'undefined') {
		return;
	}
	const now = Date.now();
	try {
		const previous = read_organization_jwt_cache_entry(data_hub_instance?.organization_id);
		const created_at =
			previous && typeof previous.created_at === 'number' ? previous.created_at : now;

		const entry = {
			jwt: jwt_string,
			created_at,
			updated_at: now,
		};
		localStorage.setItem(key, JSON.stringify(entry));
	} catch {
		// quota / private mode
	}
}

/**
 * @param {Object} data_hub_instance
 * @returns {boolean}
 */
export function has_hmac_key_pair_for_user_jwt_fetch(data_hub_instance) {
	if (!data_hub_instance?.get_iterable_user_data_via_hmac_key) {
		return false;
	}
	const url_params = data_hub_instance.url_params;
	if (!(url_params instanceof URLSearchParams)) {
		return false;
	}
	const plain_bound = new Set();
	for (const hmac_key_name of allowed_all_hmac_key_names) {
		const plain_field_name = hmac_param_name_to_plain_field_name(hmac_key_name);
		const hmac_hash = url_params.has(hmac_key_name)
			? url_params.get(hmac_key_name)
			: data_hub_instance.user_data?.[hmac_key_name] || null;
		if (!hmac_hash) {
			continue;
		}
		const identifier = url_params.has(plain_field_name)
			? url_params.get(plain_field_name)
			: data_hub_instance.user_data?.[plain_field_name] || null;
		if (!identifier) {
			continue;
		}
		if (plain_bound.has(plain_field_name)) {
			continue;
		}
		plain_bound.add(plain_field_name);
		return true;
	}
	return false;
}

/**
 * @param {Object} data_hub_instance
 * @returns {boolean}
 */
export function should_skip_network_fetch_for_cached_organization_jwt(data_hub_instance) {
	if (!data_hub_instance) {
		return false;
	}
	if (!data_hub_privacy_policy_allows_browser_storage(data_hub_instance)) {
		return false;
	}
	if (has_hmac_key_pair_for_user_jwt_fetch(data_hub_instance)) {
		return false;
	}
	const org_id = data_hub_instance.organization_id;
	const entry = read_organization_jwt_cache_entry(org_id);
	if (!entry?.jwt) {
		return false;
	}
	if (!is_organization_jwt_cache_entry_within_ttl(entry)) {
		return false;
	}
	const data = decode_unverified_jwt_data_payload(entry.jwt);
	if (!data?.global_uuid || typeof data.global_uuid !== 'string') {
		return false;
	}
	return true;
}
